import { EasingFunctions, type EasingFunctionType } from "./utils";

export interface Input {
  renderBody: Marko.Body<[number]>;
  value: number;
  duration?: number;
  delay?: number;
  easingFn?: EasingFunctionType;
}

export interface State {
  value: number;
  duration: number;
  delay: number;
  out: number;
  easingFn: EasingFunctionType;
}

/**
 * EvoMotionValue
 *
 * A provider component that provides a motion value to its children.
 *
 * Example:
 * <evo-motion-value|value| value=state.value duration=400 easingFn="snappy">
 *     <div style="opacity: ${value}">Hello World</div>
 * </evo-motion-value>
 *
 * // Run animation by updating the value
 * state.value = 1;
 *
 */
export default class EvoMotionValue extends Marko.Component<Input, State> {
  value?: number;
  duration?: number;
  delay?: number;

  onCreate(input: Input) {
    this.state = {
      value: input.value,
      duration: input.duration || 400,
      delay: input.delay || 0,
      out: input.value,
      easingFn: input.easingFn ?? "easeInQuad",
    };

    this.value = input.value;
    this.duration = input.duration;
  }

  updateValue(value: number, duration: number) {
    const start = this.state.out;
    const time = new Date().getTime();

    const ease = EasingFunctions[this.state.easingFn];

    const tick = () => {
      const elapsed = Math.min(duration, new Date().getTime() - time);
      const progress = elapsed / duration;

      const eased = ease(progress);

      this.state.out = start + (value - start) * eased;

      if (progress < 1) {
        requestAnimationFrame(tick);
      }
    };

    requestAnimationFrame(tick);
  }

  onInput(input: Input) {
    this.state.value = input.value;
    this.state.duration = input.duration || 400;
    this.state.easingFn = input.easingFn ?? "easeInQuad";
  }

  onUpdate() {
    if (
      this.value === this.state.value &&
      this.duration === this.state.duration
    ) {
      return;
    }

    this.value = this.state.value;
    this.duration = this.state.duration;
    this.delay = this.state.delay;

    setTimeout(() => {
      this.updateValue(this.state.value, this.state.duration);
    }, this.state.delay);
  }
}
